import React from 'react';
import { Tab, Tabs, Typography } from '@material-ui/core';
import { formatDateTime } from 'components/TimeAndDate';
import { WifiProfile } from '../../stores/TelematicsDevice/DeviceStateApi';
import { NearbyWifiNetwork } from '../../stores/TelematicsDevice/DeviceTelemetryApi';
import { useWifiScanContext } from '../../stores/WifiScan/WifiScanProvider';
import { NearbyNetworksPicker } from './NearbyNetworksPicker';
import { ManualNetworkForm } from './ManualNetworkForm';
import {
  INITIAL_PROFILE,
  NewProfile,
  buildWifiProfile,
  mapScannedNetworkSecurityType,
} from './wifiSetupModel';

type AddMode = 'nearby' | 'manual';

type AddNetworkSectionProps = {
  deviceId: string;
  onAddNetwork: (profile: WifiProfile) => void;
};

export const AddNetworkSection: React.FC<AddNetworkSectionProps> = ({ deviceId, onAddNetwork }) => {
  const {
    networks: scannedNetworks,
    networksUpdateTime,
    loadLastScan,
    contextStatus: wifiScanStatus,
  } = useWifiScanContext();

  const [newProfile, setNewProfile] = React.useState<NewProfile>(INITIAL_PROFILE);
  const [showPassword, setShowPassword] = React.useState(false);
  const [addMode, setAddMode] = React.useState<AddMode>('nearby');
  const [selectedScannedKey, setSelectedScannedKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    loadLastScan(deviceId);
    // Load the last scan once when the section mounts (i.e. when the dialog opens).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const hasScanData =
    wifiScanStatus.loading || scannedNetworks.length > 0 || Boolean(networksUpdateTime);
  const effectiveAddMode: AddMode = hasScanData ? addMode : 'manual';

  const handleChangeAddMode = (mode: AddMode) => {
    setAddMode(mode);
    setShowPassword(false);
  };

  const handleToggleScannedNetwork = (network: NearbyWifiNetwork, networkKey: string) => {
    setShowPassword(false);

    if (networkKey === selectedScannedKey) {
      setSelectedScannedKey(null);
      setNewProfile(INITIAL_PROFILE);
      return;
    }

    setSelectedScannedKey(networkKey);
    setNewProfile({
      ...newProfile,
      ssid: network.ssid,
      securityType: mapScannedNetworkSecurityType(network.securityType),
      password: '',
      hiddenSsid: false,
    });
  };

  const handleAdd = () => {
    if (!newProfile.ssid.trim()) {
      return;
    }

    onAddNetwork(buildWifiProfile(newProfile));
    setNewProfile(INITIAL_PROFILE);
    setSelectedScannedKey(null);
    setShowPassword(false);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom style={{ marginTop: '20px' }}>
        New network
      </Typography>

      {hasScanData && (
        <>
          <Tabs
            value={addMode}
            onChange={(_event, value) => handleChangeAddMode(value)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab value="nearby" label="From nearby" />
            <Tab value="manual" label="Enter manually" />
          </Tabs>
          {addMode === 'nearby' && networksUpdateTime && (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ marginTop: '12px' }}
              gutterBottom
            >
              Last scan: {formatDateTime(networksUpdateTime)}
            </Typography>
          )}
        </>
      )}

      <div style={{ marginTop: '12px' }}>
        {effectiveAddMode === 'nearby' ? (
          <NearbyNetworksPicker
            networks={scannedNetworks}
            loading={wifiScanStatus.loading}
            selectedKey={selectedScannedKey}
            onToggle={handleToggleScannedNetwork}
            newProfile={newProfile}
            onPasswordChange={(password) => setNewProfile({ ...newProfile, password })}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            onAdd={handleAdd}
          />
        ) : (
          <ManualNetworkForm
            newProfile={newProfile}
            onChange={setNewProfile}
            showPassword={showPassword}
            onToggleShowPassword={() => setShowPassword(!showPassword)}
            onAdd={handleAdd}
          />
        )}
      </div>
    </>
  );
};
